
import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { X, Search, Plus } from 'lucide-react';
import { toast } from 'sonner';

interface University {
  id: string;
  name_en: string;
  name_ar: string;
  city: string;
}

interface PropertyUniversitySelectorProps {
  selectedUniversities: string[];
  onUniversitiesChange: (universityIds: string[]) => void;
}

const PropertyUniversitySelector = ({ selectedUniversities, onUniversitiesChange }: PropertyUniversitySelectorProps) => {
  const { t, language } = useLanguage();
  const [universities, setUniversities] = useState<University[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUniversities, setFilteredUniversities] = useState<University[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUniversities();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() && universities.length > 0) {
      const filtered = universities.filter(uni => {
        const nameToSearch = language === 'ar' ? uni.name_ar : uni.name_en;
        const cityToSearch = uni.city || '';
        
        return (nameToSearch.toLowerCase().includes(searchQuery.toLowerCase()) ||
               cityToSearch.toLowerCase().includes(searchQuery.toLowerCase())) &&
               !selectedUniversities.includes(uni.id);
      });
      
      setFilteredUniversities(filtered);
    } else {
      setFilteredUniversities([]);
    }
  }, [searchQuery, universities, selectedUniversities, language]);

  const fetchUniversities = async () => {
    try {
      console.log('Fetching universities for PropertyUniversitySelector...');
      const { data, error } = await supabase
        .from('universities')
        .select('id, name_en, name_ar, city')
        .order('name_en');

      if (error) {
        console.error('Error fetching universities:', error);
        toast.error('Failed to load universities');
        return;
      }

      console.log('Universities fetched for PropertyUniversitySelector:', data);
      
      if (!data || data.length === 0) {
        console.warn('No universities found in database for PropertyUniversitySelector');
        setUniversities([]);
      } else {
        setUniversities(data);
      }
    } catch (error) {
      console.error('Exception fetching universities:', error);
      toast.error('Failed to load universities');
      setUniversities([]);
    } finally {
      setLoading(false);
    }
  };

  const addUniversity = (universityId: string) => {
    if (!selectedUniversities.includes(universityId)) {
      onUniversitiesChange([...selectedUniversities, universityId]);
      setSearchQuery('');
      setShowSearch(false);
    }
  };

  const removeUniversity = (universityId: string) => {
    onUniversitiesChange(selectedUniversities.filter(id => id !== universityId));
  };

  const getSelectedUniversitiesData = () => {
    return universities.filter(uni => selectedUniversities.includes(uni.id));
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <Label>{t('target_universities', 'الجامعات المستهدفة', 'Target Universities')}</Label>
        <div className="animate-pulse h-10 bg-muted rounded"></div>
      </div>
    );
  }

  if (universities.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>{t('target_universities', 'الجامعات المستهدفة', 'Target Universities')}</Label>
        </div>
        <div className="p-4 border border-dashed rounded-lg text-center">
          <p className="text-muted-foreground">
            {t('no_universities_available', 'لا توجد جامعات متاحة. يرجى إضافة الجامعات في لوحة الإدارة أولاً.', 'No universities available. Please add universities in the admin panel first.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>{t('target_universities', 'الجامعات المستهدفة', 'Target Universities')}</Label>
        <span className="text-sm text-muted-foreground">
          {t('select_universities_help', 'اختر الجامعات التي يستهدفها العقار', 'Select universities this property targets')}
        </span>
      </div>

      {/* Selected universities */}
      {selectedUniversities.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {getSelectedUniversitiesData().map((university) => (
            <Badge key={university.id} variant="secondary" className="flex items-center gap-1 px-3 py-1">
              <span className="text-sm">
                {language === 'ar' ? university.name_ar : university.name_en}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                onClick={() => removeUniversity(university.id)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}

      {/* Add university button/search */}
      {!showSearch ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowSearch(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {t('add_university', 'إضافة جامعة', 'Add University')}
        </Button>
      ) : (
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('search_universities', 'ابحث عن الجامعات...', 'Search universities...')}
              className="pl-10"
              autoFocus
            />
          </div>
          
          {/* Search results */}
          {searchQuery.trim() && filteredUniversities.length > 0 && (
            <div className="border rounded-lg max-h-48 overflow-y-auto bg-background">
              {filteredUniversities.slice(0, 10).map((university) => (
                <button
                  key={university.id}
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-muted transition-colors border-b last:border-b-0"
                  onClick={() => addUniversity(university.id)}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {language === 'ar' ? university.name_ar : university.name_en}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {university.city}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* No results message */}
          {searchQuery.trim() && filteredUniversities.length === 0 && (
            <div className="p-3 text-center text-muted-foreground border rounded-lg">
              {t('no_universities_found', 'لا توجد جامعات', 'No universities found matching your search')}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setShowSearch(false);
                setSearchQuery('');
              }}
            >
              {t('cancel', 'إلغاء', 'Cancel')}
            </Button>
          </div>
        </div>
      )}

      {selectedUniversities.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {t('no_universities_selected', 'لم يتم اختيار أي جامعات بعد', 'No universities selected yet')}
        </p>
      )}
    </div>
  );
};

export default PropertyUniversitySelector;
